import type { BufferGeometry, Color } from 'three';
import type { Pcg32 } from '../../../sim/rng';
import { MeshBuilder, place } from '../../kit/MeshBuilder';
import type { Palette } from '../../palette';

/* Every effect shape, built with the kit's MeshBuilder: flat-shaded, vertex-coloured, palette only. */

export function cubeGeometry(color: Color): BufferGeometry {
  return new MeshBuilder().box(1, 1, 1, color, undefined, true).build();
}

/** Unit-radius lump; instances scale it to size. */
export function blobGeometry(color: Color, top: Color, rng: Pcg32, squash: number, jitter: number): BufferGeometry {
  return new MeshBuilder().blob(1, squash, 1, color, undefined, { jitter, rng, topColor: top }).build();
}

/** Scorch mark: a unit-radius blot a few centimetres thick, scaled out to the blast. */
export function scorchGeometry(p: Palette, rng: Pcg32): BufferGeometry {
  return new MeshBuilder().blob(1, 0.04, 1, p.color('ink.1'), undefined, { jitter: 0.3, rng, flatBottom: true }).build();
}

/** Four-point star in the local x/y plane; instances are turned to face the camera. */
export function starGeometry(p: Palette): BufferGeometry {
  const mb = new MeshBuilder();
  mb.box(0.8, 0.14, 0.1, p.color('fire.2'), undefined, true);
  mb.box(0.14, 0.8, 0.1, p.color('fire.2'), undefined, true);
  mb.box(0.3, 0.3, 0.16, p.color('fire.3'), undefined, true);
  return mb.build();
}

/** Knife flash: a pale bar reaching out from the origin along +z, swung by its instance yaw. */
export function slashGeometry(p: Palette): BufferGeometry {
  return new MeshBuilder().box(0.1, 0.1, 0.75, p.color('paper.0'), place(0, 0, 0.75), true).build();
}

/** Tracers and rockets point along +z; instance yaw = atan2(dirX, dirZ). */
export function tracerGeometry(side: Color, top: Color): BufferGeometry {
  return new MeshBuilder().box(0.1, 0.1, 0.9, { all: side, top }, undefined, true).build();
}

export function grenadeGeometry(p: Palette): BufferGeometry {
  return new MeshBuilder().blob(0.15, 0.17, 0.15, p.color('ink.1'), place(0, 0.17, 0), { topColor: p.color('canvas.0') }).build();
}

export function rocketBodyGeometry(p: Palette): BufferGeometry {
  const mb = new MeshBuilder();
  mb.box(0.16, 0.16, 0.8, { all: p.color('metal.lo'), top: p.color('metal.mid') }, undefined, true);
  mb.box(0.2, 0.2, 0.26, p.color('canvas.0'), place(0, 0, 0.45), true); // warhead
  mb.box(0.42, 0.06, 0.16, p.color('metal.lo'), place(0, 0, -0.34), true); // fins
  mb.box(0.06, 0.42, 0.16, p.color('metal.lo'), place(0, 0, -0.34), true);
  return mb.build();
}

export function rocketTailGeometry(p: Palette): BufferGeometry {
  const mb = new MeshBuilder();
  mb.box(0.24, 0.24, 0.5, p.color('fire.2'), place(0, 0, -0.65), true);
  mb.box(0.14, 0.14, 0.3, p.color('fire.3'), place(0, 0, -1.0), true);
  return mb.build();
}

export function bombBodyGeometry(p: Palette): BufferGeometry {
  const mb = new MeshBuilder();
  mb.box(0.42, 0.22, 0.3, { all: p.color('canvas.0'), top: p.color('canvas.1') });
  mb.box(0.1, 0.24, 0.32, p.color('ink.1'), place(-0.08, 0, 0)); // strap
  mb.box(0.16, 0.06, 0.16, p.color('ink.1'), place(0.1, 0.22, 0)); // lamp housing
  return mb.build();
}

export function bombLampGeometry(p: Palette): BufferGeometry {
  return new MeshBuilder().box(0.13, 0.13, 0.13, p.color('fire.0'), place(0.1, 0.26, 0)).build();
}

/** Bark marker, hostile: a box-built "!" 1.2 m tall, standing upright on its origin. */
export function alertGeometry(p: Palette): BufferGeometry {
  const mb = new MeshBuilder();
  mb.box(0.24, 0.26, 0.24, p.color('enemyAccent.0'));
  mb.box(0.24, 0.8, 0.24, p.color('enemyAccent.0'), place(0, 0.4, 0));
  return mb.build();
}

/** Bark marker, unsure: a small diamond (an undivided octahedron) standing on its point. */
export function queryGeometry(p: Palette): BufferGeometry {
  return new MeshBuilder().blob(0.24, 0.32, 0.24, p.color('playerAccent.0'), place(0, 0.32, 0), { detail: 0 }).build();
}
